// "Devolver venta" en Farmacia Interna (Fase 8, PR 8.2) — mismas 3-4 situaciones que ya tiene el CRM
// (facturas-devolucion-cf.tsx / facturas-devolucion-identificada.tsx), reconstruidas acá EN EL ESTILO de
// ComprobantesFarmacia.tsx: componentes propios (Dialog/Button/Input/Select de '@/components/ui', no Mantine ni
// @medplum/react). La lógica de negocio (qué ruta aplica, qué reversa qué) NUNCA se reimplementa acá: el servidor
// ya la calculó (`FacturaFarmacia.ruta`, `lib/farmacia-devolucion.ts`, que reusa `calcularRutaDevolucion`) y la
// vuelve a validar al confirmar cualquiera de las acciones (mismas funciones que usa el CRM).
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, Loader2, Receipt, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  anulacionEnLineaIniciarFarmacia,
  anulacionEnLineaVerificarFarmacia,
  devolucionConsumidorFinalFarmacia,
  devolucionNotaCreditoFarmacia,
  FORMAS_REEMBOLSO_FARMACIA,
  type FacturaFarmacia,
  type FormaReembolsoFarmacia,
} from '@/lib/farmaciaInterna';

function formatoFecha(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Campo copiable (clave de acceso / fecha) para pegar en el portal SRI en Línea. */
function CampoCopiable({ etiqueta, valor }: { etiqueta: string; valor: string }): JSX.Element {
  const { t } = useTranslation();
  const [copiado, setCopiado] = useState(false);
  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      // Sin permiso del navegador: el valor sigue visible para copiarlo a mano.
    }
  }
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold text-hce-ink">{etiqueta}</p>
      <div className="flex items-center gap-2 rounded-md border border-border bg-hce-paper px-2.5 py-1.5">
        <code className="flex-1 truncate text-xs text-hce-ink">{valor}</code>
        <Button size="sm" variant="ghost" onClick={() => void copiar()}>
          {copiado ? t('farmaciaApp.comprobantes.devolver.copiado') : t('farmaciaApp.comprobantes.devolver.copiar')}
        </Button>
      </div>
    </div>
  );
}

/** Banner de la fila cuando ya hay una anulación en línea en trámite — sin modal, el resultado se lee en el acto. */
function BannerAnulacionEnCurso({ invoiceId, onCambio }: { invoiceId: number; onCambio: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation();
  const [verificando, setVerificando] = useState(false);
  const [mensaje, setMensaje] = useState<string>();

  async function onVerificar(): Promise<void> {
    setVerificando(true);
    setMensaje(undefined);
    try {
      const r = await anulacionEnLineaVerificarFarmacia(invoiceId);
      if (!r.ok) {
        if (r.accion === 'transporte') {
          // No se pudo consultar al SRI: `anulacionEnCurso` sigue en `true` tal cual estaba, nada que refrescar.
          setMensaje(t('farmaciaApp.comprobantes.devolver.verificarTransporte'));
          return;
        }
        // `revision_manual`: el servidor YA puso `anulacionEnCurso=false` y dejó de reintentar (ver
        // anulacion-en-linea.ts, `marcarRevisionManual`) — sin `onCambio()` acá, este banner queda estancado para
        // siempre (esta pantalla no tiene polling/react-query como el CRM que refresque solo). Hallazgo de la
        // revisión independiente de PR 8.2, ronda 2.
        setMensaje(t('farmaciaApp.comprobantes.devolver.verificarRevisionManual', { motivo: r.motivo }));
        await onCambio();
        return;
      }
      if (r.accion === 'nada_que_verificar') setMensaje(t('farmaciaApp.comprobantes.devolver.verificarNadaQueVerificar'));
      else if (r.accion === 'sigue_pendiente') setMensaje(t('farmaciaApp.comprobantes.devolver.verificarSiguePendiente'));
      else {
        setMensaje(r.reversaPagoFallida ? t('farmaciaApp.comprobantes.devolver.verificarAnuladaPagoNoReversado') : t('farmaciaApp.comprobantes.devolver.verificarAnulada'));
        await onCambio();
      }
    } finally {
      setVerificando(false);
    }
  }

  return (
    <div className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
      <p className="flex items-start gap-1.5 font-medium text-hce-ink">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
        <span>
          <strong className="block">{t('farmaciaApp.comprobantes.devolver.anulacionEnCursoTitulo')}</strong>
          {t('farmaciaApp.comprobantes.devolver.anulacionEnCursoTexto')}
        </span>
      </p>
      {mensaje && <p className="text-hce-ink">{mensaje}</p>}
      <Button size="sm" variant="secondary" disabled={verificando} onClick={() => void onVerificar()}>
        {verificando && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        {verificando ? t('farmaciaApp.comprobantes.devolver.verificando') : t('farmaciaApp.comprobantes.devolver.botonVerificar')}
      </Button>
    </div>
  );
}

/** Modal — ruta CONSUMIDOR FINAL: aviso legal fijo + motivo + forma de reembolso. */
function ModalDevolucionCf({ invoiceId, onCerrar, onCambio }: { invoiceId: number; onCerrar: () => void; onCambio: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState('');
  const [formaReembolso, setFormaReembolso] = useState<FormaReembolsoFarmacia>('efectivo');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const [listo, setListo] = useState<string>();

  async function confirmar(): Promise<void> {
    const motivoLimpio = motivo.trim();
    if (!motivoLimpio) {
      setError(t('farmaciaApp.comprobantes.devolver.motivoRequerido'));
      return;
    }
    setEnviando(true);
    setError(undefined);
    try {
      const r = await devolucionConsumidorFinalFarmacia(invoiceId, { motivo: motivoLimpio, formaReembolso });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setListo(r.datos.numeroConstancia);
      await onCambio();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !enviando && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('farmaciaApp.comprobantes.devolver.tituloCf')}</DialogTitle>
        </DialogHeader>
        {listo ? (
          <div className="space-y-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-sm">
            <p className="font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.cfListoTitulo', { numero: listo })}</p>
            <p className="text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.cfListoDetalle')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p role="alert" className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs font-medium text-hce-ink">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <span>{t('farmaciaApp.comprobantes.devolver.avisoLegalCf')}</span>
            </p>
            <div>
              <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.campoMotivoCf')}</label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.campoFormaReembolso')}</label>
              <Select value={formaReembolso} onValueChange={(v) => setFormaReembolso(v as FormaReembolsoFarmacia)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMAS_REEMBOLSO_FARMACIA.map((f) => (
                    <SelectItem key={f} value={f}>
                      {t(`farmaciaApp.comprobantes.devolver.formaReembolso.${f}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}
        <DialogFooter>
          {listo ? (
            <Button onClick={onCerrar}>{t('farmaciaApp.comprobantes.devolver.cerrar')}</Button>
          ) : (
            <>
              <Button variant="ghost" disabled={enviando} onClick={onCerrar}>
                {t('farmaciaApp.comprobantes.devolver.cancelar')}
              </Button>
              <Button disabled={enviando} onClick={() => void confirmar()}>
                {enviando && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
                {enviando ? t('farmaciaApp.comprobantes.devolver.confirmando') : t('farmaciaApp.comprobantes.devolver.confirmarDevolucion')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Paso = 'elegir' | 'anular' | 'nota_credito';

/** Modal — ruta IDENTIFICADA: dentro de plazo (dos opciones) o fuera de plazo (solo nota de crédito). */
function ModalDevolucionIdentificada({
  invoiceId,
  ruta,
  vencePlazo,
  onCerrar,
  onCambio,
}: {
  invoiceId: number;
  ruta: 'dentro_de_plazo' | 'fuera_de_plazo';
  vencePlazo: string | null;
  onCerrar: () => void;
  onCambio: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [paso, setPaso] = useState<Paso>(ruta === 'fuera_de_plazo' ? 'nota_credito' : 'elegir');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const [listoAnular, setListoAnular] = useState<{ claveAcceso: string; fechaEmision: string; fechaAutorizacion: string | null }>();
  const [listoNc, setListoNc] = useState(false);
  const terminado = listoAnular != null || listoNc;

  async function confirmarAnular(): Promise<void> {
    setEnviando(true);
    setError(undefined);
    try {
      const r = await anulacionEnLineaIniciarFarmacia(invoiceId);
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setListoAnular(r.datos);
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarNotaCredito(): Promise<void> {
    const motivoLimpio = motivo.trim();
    if (!motivoLimpio) {
      setError(t('farmaciaApp.comprobantes.devolver.motivoRequerido'));
      return;
    }
    setEnviando(true);
    setError(undefined);
    try {
      const r = await devolucionNotaCreditoFarmacia(invoiceId, motivoLimpio);
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setListoNc(true);
      await onCambio();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !enviando && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('farmaciaApp.comprobantes.devolver.titulo')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {listoAnular && (
            <div className="space-y-2">
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5">
                <p className="font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.anulacionIniciadaTitulo')}</p>
                <p className="text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.anulacionIniciadaDetalle')}</p>
              </div>
              <CampoCopiable etiqueta={t('farmaciaApp.comprobantes.devolver.campoClaveAcceso')} valor={listoAnular.claveAcceso} />
              <CampoCopiable etiqueta={t('farmaciaApp.comprobantes.devolver.campoFechaEmision')} valor={listoAnular.fechaEmision} />
              {listoAnular.fechaAutorizacion && (
                <CampoCopiable etiqueta={t('farmaciaApp.comprobantes.devolver.campoFechaAutorizacion')} valor={formatoFecha(listoAnular.fechaAutorizacion)} />
              )}
            </div>
          )}

          {listoNc && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5">
              <p className="font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.notaCreditoListoTitulo')}</p>
              <p className="text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.notaCreditoListoDetalle')}</p>
            </div>
          )}

          {!terminado && paso === 'elegir' && (
            <>
              <p className="text-xs font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.preguntaRuta')}</p>
              {ruta === 'dentro_de_plazo' && vencePlazo && (
                <p className="text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.vencePlazoTexto', { fecha: formatoFecha(vencePlazo) })}</p>
              )}
              <button
                type="button"
                onClick={() => setPaso('anular')}
                className="flex w-full items-start gap-2.5 rounded-md border border-border px-3 py-2.5 text-left hover:bg-hce-paper"
              >
                <Ban className="mt-0.5 h-4 w-4 shrink-0 text-hce-ink" aria-hidden="true" />
                <span>
                  <span className="block text-sm font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.opcionAnularTitulo')}</span>
                  <span className="block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.opcionAnularExplicacion')}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setPaso('nota_credito')}
                className="flex w-full items-start gap-2.5 rounded-md border border-border px-3 py-2.5 text-left hover:bg-hce-paper"
              >
                <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-hce-ink" aria-hidden="true" />
                <span>
                  <span className="block text-sm font-semibold text-hce-ink">{t('farmaciaApp.comprobantes.devolver.opcionNotaCreditoTitulo')}</span>
                  <span className="block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.opcionNotaCreditoExplicacion')}</span>
                </span>
              </button>
            </>
          )}

          {!terminado && paso === 'anular' && (
            <p role="alert" className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs font-medium text-hce-ink">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <span>{t('farmaciaApp.comprobantes.devolver.opcionAnularExplicacion')}</span>
            </p>
          )}

          {!terminado && paso === 'nota_credito' && (
            <>
              {ruta === 'fuera_de_plazo' && (
                <p role="alert" className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs font-medium text-hce-ink">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  <span>{t('farmaciaApp.comprobantes.devolver.fueraDePlazoAviso')}</span>
                </p>
              )}
              <div>
                <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.devolver.campoMotivo')}</label>
                <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300} />
              </div>
            </>
          )}

          {!terminado && error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          {terminado ? (
            <Button onClick={onCerrar}>{t('farmaciaApp.comprobantes.devolver.cerrar')}</Button>
          ) : (
            <>
              {paso === 'elegir' && (
                <Button variant="ghost" onClick={onCerrar}>
                  {t('farmaciaApp.comprobantes.devolver.cancelar')}
                </Button>
              )}
              {paso !== 'elegir' && ruta === 'dentro_de_plazo' && (
                <Button
                  variant="ghost"
                  disabled={enviando}
                  onClick={() => {
                    setPaso('elegir');
                    setError(undefined);
                  }}
                >
                  {t('farmaciaApp.comprobantes.devolver.volver')}
                </Button>
              )}
              {paso !== 'elegir' && ruta === 'fuera_de_plazo' && (
                <Button variant="ghost" disabled={enviando} onClick={onCerrar}>
                  {t('farmaciaApp.comprobantes.devolver.cancelar')}
                </Button>
              )}
              {paso === 'anular' && (
                <Button disabled={enviando} onClick={() => void confirmarAnular()}>
                  {enviando && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {enviando ? t('farmaciaApp.comprobantes.devolver.confirmandoAnular') : t('farmaciaApp.comprobantes.devolver.confirmarAnular')}
                </Button>
              )}
              {paso === 'nota_credito' && (
                <Button disabled={enviando} onClick={() => void confirmarNotaCredito()}>
                  {enviando && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {enviando ? t('farmaciaApp.comprobantes.devolver.confirmandoNotaCredito') : t('farmaciaApp.comprobantes.devolver.confirmarNotaCredito')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Devolver venta" de una fila de Comprobantes de Farmacia — el llamador (`ComprobantesFarmacia.tsx`) pasa la
 * factura tal cual la trajo el servidor; esta función decide QUÉ pintar según `ruta`/`anulacionEnCurso`
 * (`FacturaFarmacia`, ya calculados por `lib/farmacia-devolucion.ts`). Nunca se pinta para una factura sin ruta
 * (`ruta === null`: no emitida todavía).
 */
export function DevolverVentaFarmacia({ factura, onCambio }: { factura: FacturaFarmacia; onCambio: () => Promise<void> }): JSX.Element | null {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);

  if (factura.anulacionEnCurso) return <BannerAnulacionEnCurso invoiceId={factura.id} onCambio={onCambio} />;
  if (factura.ruta === null) return null;

  if (factura.ruta === 'bloqueada') {
    return (
      <p className="rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-xs font-medium text-hce-ink">
        <strong className="block">{t('farmaciaApp.comprobantes.devolver.bloqueadaTitulo')}</strong>
        {factura.motivoBloqueo}
      </p>
    );
  }

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setAbierto(true)}>
        <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        {t('farmaciaApp.comprobantes.devolver.boton')}
      </Button>
      {abierto &&
        (factura.ruta === 'consumidor_final' ? (
          <ModalDevolucionCf invoiceId={factura.id} onCerrar={() => setAbierto(false)} onCambio={onCambio} />
        ) : (
          <ModalDevolucionIdentificada invoiceId={factura.id} ruta={factura.ruta} vencePlazo={factura.vencePlazo} onCerrar={() => setAbierto(false)} onCambio={onCambio} />
        ))}
    </>
  );
}
